import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Download,
  SlidersHorizontal,
  Copy,
  AlertTriangle,
  RefreshCw,
  CheckCircle2,
} from "lucide-react";
import { listQueueReports, type CurrentUser, type QueueReportItem } from "../../lib/api";

type QRMessageQueueViewProps = {
  token: string;
  user: CurrentUser;
  tenantId?: string;
};

export function QRMessageQueueView({ token, user: _user, tenantId }: QRMessageQueueViewProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [connFilter, setConnFilter] = useState("all");

  const queueQuery = useQuery({
    queryKey: ["reports", "queue", tenantId],
    queryFn: () => listQueueReports(token, tenantId),
    refetchInterval: 5_000,
  });

  const queueItems: QueueReportItem[] = queueQuery.data || [];

  const filteredQueue = queueItems.filter((item) => {
    const matchesSearch =
      item.user_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.instance_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.instance_key.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.destination && item.destination.includes(searchQuery));
    const matchesConn = connFilter === "all" || item.connection_state === connFilter;
    return matchesSearch && matchesConn;
  });

  function copyText(text: string) {
    navigator.clipboard.writeText(text);
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Message Queue Telemetry</h1>
          <p className="text-xs text-slate-500 mt-1">
            Real-time live message dispatch pipeline, socket states, and pending outbound batches.
          </p>
        </div>

        <button
          onClick={() => queueQuery.refetch()}
          className="btn-secondary text-xs py-2 px-3 flex items-center gap-1.5 shadow-2xs"
        >
          <RefreshCw className={`w-3.5 h-3.5 text-slate-500 ${queueQuery.isFetching ? "animate-spin" : ""}`} />
          <span>Sync Queue</span>
        </button>
      </div>

      {/* Filter Bar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {/* Search Box */}
          <div className="relative w-64">
            <input
              type="text"
              placeholder="Search destination, instance..."
              className="modern-input w-full text-xs py-2"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Connection Filter */}
          <select
            value={connFilter}
            onChange={(e) => setConnFilter(e.target.value)}
            className="filter-chip cursor-pointer outline-none"
          >
            <option value="all">Connection: All Connections</option>
            <option value="connected">Connected Only</option>
            <option value="disconnected">Disconnected Only</option>
          </select>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          <button className="btn-secondary text-xs py-2 px-3.5 flex items-center gap-1.5 shadow-2xs">
            <Download className="w-3.5 h-3.5 text-slate-500" />
            <span>Export CSV</span>
          </button>

          <button className="btn-secondary text-xs py-2 px-3 flex items-center gap-1.5 shadow-2xs">
            <SlidersHorizontal className="w-3.5 h-3.5 text-slate-500" />
            <span>Columns</span>
          </button>
        </div>
      </div>

      {/* Message Queue Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-2xs">
        <div className="overflow-x-auto">
          <table className="modern-table">
            <thead>
              <tr>
                <th>Organization</th>
                <th>Instance</th>
                <th>Recipient / Channel</th>
                <th>Socket Status</th>
                <th>Queue Timestamp</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredQueue.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-slate-400 text-xs">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <CheckCircle2 className="w-8 h-8 text-emerald-500/80" />
                      <span className="font-semibold text-slate-700">Outbound queue is currently empty!</span>
                      <span className="text-[11px] text-slate-400">All WhatsApp messages have been processed or dispatched cleanly.</span>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredQueue.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50/70 transition-colors">
                    <td>
                      <div className="font-semibold text-slate-800 text-xs">{item.tenant_name}</div>
                    </td>
                    <td>
                      <div className="font-semibold text-slate-900 text-xs">{item.instance_name}</div>
                      <div className="flex items-center gap-1 text-[11px] font-mono text-slate-500">
                        <button onClick={() => copyText(item.instance_key)} className="hover:text-slate-900" title="Copy Key">
                          <Copy className="w-3 h-3 text-slate-400" />
                        </button>
                        <span>{item.instance_key}</span>
                      </div>
                    </td>
                    <td className="text-xs font-mono text-slate-700">{item.destination || item.connection_detail}</td>
                    <td>
                      <span
                        className={`status-pill ${
                          item.connection_state === "connected"
                            ? "status-pill--connected"
                            : "status-pill--disconnected"
                        }`}
                      >
                        {item.connection_state === "connected" ? "Connected" : "Disconnected"}
                      </span>
                    </td>
                    <td className="font-mono text-xs text-slate-600">{item.queue_date}</td>
                    <td>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-amber-50 text-amber-700 border border-amber-200">
                        <AlertTriangle className="w-3 h-3 text-amber-600" />
                        Queued
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
